/** Celebration engine — workspace-configurable confetti + sound + flash message. */
import confetti from "canvas-confetti";
import api from "@/lib/api";

let _wsId = null;
let _cache = null;
let _cacheAt = 0;

export function setCelebrationWorkspace(wsId) {
  if (_wsId !== wsId) { _wsId = wsId; _cache = null; }
}
export function invalidateCelebrationConfig() { _cache = null; }

export function isCelebrationMuted() {
  try { return localStorage.getItem("acq_celebrate_mute") === "1"; } catch { return false; }
}
export function setCelebrationMuted(v) {
  try { localStorage.setItem("acq_celebrate_mute", v ? "1" : "0"); } catch { /* ignore */ }
}

export async function getCelebrationConfig() {
  if (!_wsId) return null;
  if (_cache && Date.now() - _cacheAt < 300000) return _cache;
  const { data } = await api.get(`/workspaces/${_wsId}/celebrations/config`);
  _cache = data; _cacheAt = Date.now();
  return data;
}

// ── Sounds: tiny WebAudio synth, no audio assets needed ──
const SOUND_NOTES = {
  chime:   [[880, 0, 0.18], [1318, 0.14, 0.35]],
  coins:   [[988, 0, 0.07], [1319, 0.08, 0.07], [1568, 0.16, 0.07], [2093, 0.24, 0.22]],
  fanfare: [[523, 0, 0.16], [659, 0.16, 0.16], [784, 0.32, 0.16], [1047, 0.48, 0.45]],
  tada:    [[523, 0, 0.12], [784, 0.1, 0.12], [1047, 0.2, 0.5]],
  pop:     [[440, 0, 0.06], [660, 0.05, 0.09]],
};

export function playCelebrationSound(name) {
  if (!name || name === "none" || isCelebrationMuted()) return;
  const notes = SOUND_NOTES[name];
  if (!notes) return;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    notes.forEach(([freq, t, dur]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = name === "coins" ? "square" : "triangle";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + t);
      gain.gain.exponentialRampToValueAtTime(0.16, ctx.currentTime + t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + dur);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(ctx.currentTime + t);
      osc.stop(ctx.currentTime + t + dur + 0.05);
    });
    setTimeout(() => ctx.close().catch(() => {}), 2500);
  } catch { /* audio unavailable */ }
}

// ── Confetti styles ──
const GOLD = ["#f59e0b", "#fbbf24", "#fde68a", "#ffffff"];

export function fireConfetti(style) {
  if (!style || style === "none") return;
  if (style === "burst") {
    confetti({ particleCount: 130, spread: 80, origin: { y: 0.7 }, zIndex: 9999 });
  } else if (style === "fireworks") {
    const end = Date.now() + 1800;
    const tick = () => {
      confetti({ particleCount: 45, startVelocity: 32, spread: 360, ticks: 60, zIndex: 9999,
        origin: { x: 0.15 + Math.random() * 0.7, y: 0.15 + Math.random() * 0.4 } });
      if (Date.now() < end) setTimeout(tick, 280);
    };
    tick();
  } else if (style === "rain") {
    for (let i = 0; i < 3; i++) {
      setTimeout(() => confetti({ particleCount: 90, spread: 160, startVelocity: 14, gravity: 0.65,
        ticks: 320, origin: { x: Math.random(), y: -0.1 }, zIndex: 9999 }), i * 350);
    }
  } else if (style === "stars") {
    const opts = { spread: 360, ticks: 70, gravity: 0.35, decay: 0.95, startVelocity: 28,
      shapes: ["star"], colors: GOLD, zIndex: 9999 };
    confetti({ ...opts, particleCount: 50, origin: { y: 0.45 } });
    setTimeout(() => confetti({ ...opts, particleCount: 35, scalar: 0.8, origin: { y: 0.4 } }), 250);
  }
}

function renderMessage(template, vars) {
  return (template || "").replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined && vars[k] !== null ? vars[k] : m));
}

export function previewCelebration(eventCfg, vars = {}) {
  fireConfetti(eventCfg.confetti);
  playCelebrationSound(eventCfg.sound);
  const message = renderMessage(eventCfg.message, vars);
  if (message) {
    window.dispatchEvent(new CustomEvent("acq-celebrate", { detail: { message, duration: eventCfg.duration_ms || 3500 } }));
  }
}

/** Main entry: celebrate("task_completed", { title: "Design homepage" }) */
export async function celebrate(eventKey, vars = {}) {
  let cfg = null;
  try { cfg = await getCelebrationConfig(); } catch { return; }
  const ev = cfg?.events?.[eventKey];
  if (!ev || ev.enabled === false) return;
  previewCelebration(ev, vars);
}
